import React, { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard,
  Boxes,
  ClipboardCheck,
  Wrench,
  LineChart,
  FileBadge,
  Map,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
} from 'lucide-react';

const SLIDES = [
  {
    slug: 'dashboard',
    icon: LayoutDashboard,
    label: 'Command center',
    title: 'Fleet status at a glance',
    body: 'Total assets, open defects, work orders due, services due. Fleet Compliance %, worst-performing assets, and a one-tap launchpad for everything your crew runs each morning.',
    img: '/tour/dashboard.png',
    callouts: [
      { x: 56, y: 27, anchor: 'top',    overline: 'Live fleet metrics',  label: 'Assets, defects, WOs, services — refreshed in real time' },
      { x: 38, y: 62, anchor: 'left',   overline: 'Compliance headline', label: '% compliant + worst-performing drill-down' },
      { x: 78, y: 78, anchor: 'right',  overline: 'Triage feeds',        label: 'Recent defects + pending WOs front-and-centre' },
    ],
  },
  {
    slug: 'fleet-map',
    icon: Map,
    label: 'Fleet Map · Geofences',
    title: 'Every machine on the map. Live.',
    body: 'GPS positions refreshed every 30 seconds. Marker clustering for fleets of any size. Draw polygon geofences on the map — Guardian auto-detects enter/exit and alerts your admins in real time.',
    img: '/tour/fleet-map.png',
    callouts: [
      { x: 33, y: 23, anchor: 'left',   overline: 'Live + stale + offline', label: 'Status pills count assets reporting right now' },
      { x: 70, y: 16, anchor: 'left',   overline: 'Draw geofence',          label: 'Outline a job site — alerts fire on enter / exit' },
      { x: 47, y: 52, anchor: 'bottom', overline: 'Clustered markers',      label: 'Hundreds of assets stay readable at any zoom' },
    ],
  },
  {
    slug: 'assets',
    icon: Boxes,
    label: 'Asset register',
    title: 'Every machine, every component',
    body: 'Main assets and auxiliary equipment (slings, shackles, chains) with SWL ratings, serial numbers, IMEI-linked telematics and parent linkage. CSV bulk-import up to 500 at once.',
    img: '/tour/assets.png',
    callouts: [
      { x: 28, y: 27, anchor: 'left',   overline: 'Main vs Auxiliary',  label: 'Filter cranes from slings + shackles with one click' },
      { x: 70, y: 11, anchor: 'left',   overline: 'CSV import',         label: 'Up to 500 assets in under a minute' },
      { x: 36, y: 40, anchor: 'bottom', overline: 'IMEI telematics',    label: 'Live GPS-enabled assets show their IMEI inline' },
    ],
  },
  {
    slug: 'inspections',
    icon: ClipboardCheck,
    label: 'Inspections',
    title: 'Pre-start checklists, signed in the field',
    body: 'Pass / fail / N-A per item, photo capture, GPS-tagged. Failed items auto-create severity-graded defects. Inspector signature locks the record.',
    img: '/tour/inspections.png',
    callouts: [
      { x: 28, y: 35, anchor: 'left',   overline: 'Status at a glance',  label: 'Pass · Fail · In-progress · auto-coloured' },
      { x: 70, y: 32, anchor: 'right',  overline: 'Asset linkage',       label: 'Every inspection tied to its asset + operator' },
      { x: 50, y: 78, anchor: 'bottom', overline: 'Auto-defects',        label: 'Failed items raise severity-graded tickets instantly' },
    ],
  },
  {
    slug: 'maintenance',
    icon: CalendarClock,
    label: 'Preventative maintenance',
    title: 'Service intervals that police themselves',
    body: 'Time-based and usage-based PM schedules per asset. Guardian flags overdue services in red, links each schedule to its work order, and bulk-applies templates across a whole category in seconds.',
    img: '/tour/maintenance.png',
    callouts: [
      { x: 24, y: 36, anchor: 'left',   overline: 'Hours or calendar', label: 'Every 500 hrs · every 90 days' },
      { x: 70, y: 42, anchor: 'left',   overline: 'Overdue triage',    label: 'Red status the moment the interval lapses' },
      { x: 48, y: 78, anchor: 'bottom', overline: 'Work-order linked', label: 'One click turns a due service into a scheduled WO' },
    ],
  },
  {
    slug: 'workshop',
    icon: Wrench,
    label: 'Workshop',
    title: 'Workshop command center',
    body: 'Overdue · In-Progress · Upcoming · Defects, every work order tile on one wall-screen. Standalone client-ID auth so the workshop dashboard stays on a TV without exposing the full app.',
    img: '/tour/workshop.png',
    callouts: [
      { x: 17, y: 14, anchor: 'bottom', overline: 'Overdue triage',  label: 'Red columns = anything past its scheduled date' },
      { x: 86, y: 16, anchor: 'left',   overline: 'Defects feed',    label: 'Severity-graded · 20 open at a glance' },
      { x: 50, y: 95, anchor: 'top',    overline: 'TV-friendly',     label: 'Runs landscape on workshop screens 24/7' },
    ],
  },
  {
    slug: 'insights',
    icon: LineChart,
    label: 'Cost insights',
    title: 'Cost per operating hour, fleet-wide',
    body: 'Labour (per-user hourly rate) + parts + misc, tracked per work order. Asset health score, total cost, hours worked, and current defect / cert status — all on one page per asset.',
    img: '/tour/insights.png',
    callouts: [
      { x: 16, y: 34, anchor: 'bottom', overline: 'Health score',     label: 'Per-asset rating from defects + WO history' },
      { x: 29, y: 60, anchor: 'left',   overline: 'Cost breakdown',   label: 'Labour · parts · misc — to the cent' },
      { x: 40, y: 80, anchor: 'top',    overline: 'Current status',   label: 'Open WOs · open defects · expiring certs at the bottom' },
    ],
  },
  {
    slug: 'certificates',
    icon: FileBadge,
    label: 'Certificates',
    title: 'Compliance, calendared',
    body: 'Every certificate auto-flagged 30 days before expiry. Valid / Expiring / Expired counts at the top, per-asset register below. In-app + email alerts to your safety officer.',
    img: '/tour/certificates.png',
    callouts: [
      { x: 30, y: 20, anchor: 'bottom', overline: 'Health summary',  label: 'Valid · Expiring · Expired — counts on the hero strip' },
      { x: 88, y: 41, anchor: 'left',   overline: 'Status badges',   label: 'Yellow at 30 days · red on expiry · sorted soonest-first' },
      { x: 22, y: 30, anchor: 'bottom', overline: 'Filter & search', label: 'Cut the 217-cert register by type, asset, status' },
    ],
  },
];

const ADVANCE_MS = 5500;

export default function ProductTour() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [manualPause, setManualPause] = useState(false); // explicit toggle wins over hover
  const [imgLoaded, setImgLoaded] = useState({});
  const timerRef = useRef(null);

  const effectivePaused = manualPause || paused;

  useEffect(() => {
    if (effectivePaused) return;
    timerRef.current = setTimeout(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, ADVANCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, effectivePaused]);

  const goto = (i) => {
    setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
  };
  const prev = () => goto(active - 1);
  const next = () => goto(active + 1);

  const Slide = SLIDES[active];
  const ActiveIcon = Slide.icon;

  return (
    <section
      id="tour"
      className="relative z-10 border-y border-white/5 bg-black/30"
      data-testid="product-tour"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="label-overline mb-3" data-testid="tour-overline">
              45-second tour
            </div>
            <h2
              className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight"
              data-testid="tour-headline"
            >
              See Guardian in <span className="text-[#FFD108]">eight screens</span>.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setManualPause((p) => !p)}
              className="flex items-center gap-2 h-9 px-3 rounded-sm border border-white/15 hover:border-[#FFD108]/40 hover:bg-white/[0.04] text-xs uppercase tracking-widest text-muted-foreground transition-colors"
              data-testid="tour-pause-toggle"
              aria-label={manualPause ? 'Resume tour' : 'Pause tour'}
            >
              {manualPause ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
              {manualPause ? 'Play' : 'Pause'}
            </button>
            <span
              className="font-mono text-xs text-muted-foreground hidden sm:inline-block"
              data-testid="tour-counter"
            >
              {String(active + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Screenshot frame */}
          <div className="lg:col-span-8" data-testid="tour-frame">
            <div
              className="relative rounded-sm border border-white/10 bg-[#0d0d0d] overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              {/* Browser-chrome bar */}
              <div className="flex items-center gap-2 px-4 h-9 border-b border-white/8 bg-black/40">
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
                <div className="ml-4 px-3 h-5 rounded-sm bg-white/[0.05] border border-white/8 flex items-center gap-2">
                  <span className="text-[10px] font-mono text-muted-foreground">guardian.equiprsoftware.com{SLIDES[active].slug === 'dashboard' ? '/dashboard' : `/${SLIDES[active].slug}`}</span>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-[#FFD108]/80 font-mono flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFD108] animate-pulse" />
                  LIVE
                </span>
              </div>

              {/* Image stage with fixed aspect ratio to prevent layout shift */}
              <div className="relative aspect-[1600/1000] bg-[#0a0a0a]">
                {SLIDES.map((s, i) => (
                  <div
                    key={s.slug}
                    className={`absolute inset-0 transition-opacity duration-700 ${
                      i === active ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                    aria-hidden={i !== active}
                  >
                    <img
                      src={s.img}
                      alt={s.title}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      onLoad={() => setImgLoaded((m) => ({ ...m, [s.slug]: true }))}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      data-testid={`tour-image-${s.slug}`}
                    />
                    {/* Dim overlay so callouts pop without obscuring the screenshot */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 0%, rgba(0,0,0,0.35) 100%)',
                      }}
                    />
                    {/* Callouts — only render on active slide for cleanliness; hide on small screens */}
                    {i === active && (s.callouts || []).map((c, idx) => (
                      <Callout
                        key={`${s.slug}-c-${idx}`}
                        {...c}
                        slideSlug={s.slug}
                        index={idx}
                      />
                    ))}
                  </div>
                ))}

                {/* Loading shimmer for active slide */}
                {!imgLoaded[Slide.slug] && (
                  <div className="absolute inset-0 z-20 grid place-items-center text-muted-foreground text-xs font-mono">
                    Loading {Slide.slug}…
                  </div>
                )}

                {/* Prev / Next */}
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Previous slide"
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 rounded-sm bg-black/60 border border-white/10 hover:bg-black/80 hover:border-[#FFD108]/40 grid place-items-center text-white/80 hover:text-[#FFD108] transition-colors"
                  data-testid="tour-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Next slide"
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 rounded-sm bg-black/60 border border-white/10 hover:bg-black/80 hover:border-[#FFD108]/40 grid place-items-center text-white/80 hover:text-[#FFD108] transition-colors"
                  data-testid="tour-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Progress strip */}
              <div className="relative h-0.5 bg-white/5">
                <div
                  key={`bar-${active}-${effectivePaused}`}
                  className="absolute inset-y-0 left-0 bg-[#FFD108]"
                  style={{
                    width: effectivePaused ? `${((active + 1) / SLIDES.length) * 100}%` : '100%',
                    animation: effectivePaused ? 'none' : `tour-progress ${ADVANCE_MS}ms linear forwards`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Side panel: title + body + thumbnail strip */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm bg-[#FFD108]/10 border border-[#FFD108]/30 grid place-items-center">
                <ActiveIcon className="w-5 h-5 text-[#FFD108]" strokeWidth={2} />
              </div>
              <div className="label-overline text-[#FFD108]/90">{Slide.label}</div>
            </div>
            <h3
              key={`title-${active}`}
              className="font-heading font-bold text-2xl mb-3 leading-tight"
              data-testid="tour-title"
            >
              {Slide.title}
            </h3>
            <p
              key={`body-${active}`}
              className="text-sm text-muted-foreground leading-relaxed mb-8"
              data-testid="tour-body"
            >
              {Slide.body}
            </p>

            {/* Thumbnail strip */}
            <div className="grid grid-cols-4 gap-2" data-testid="tour-thumbs">
              {SLIDES.map((s, i) => {
                const ThumbIcon = s.icon;
                return (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => goto(i)}
                    aria-label={`Go to ${s.label}`}
                    className={`group flex flex-col items-start gap-2 p-2.5 rounded-sm border text-left transition-all ${
                      i === active
                        ? 'border-[#FFD108]/50 bg-[#FFD108]/[0.06]'
                        : 'border-white/8 bg-white/[0.02] hover:border-white/20'
                    }`}
                    data-testid={`tour-thumb-${s.slug}`}
                  >
                    <ThumbIcon
                      className={`w-3.5 h-3.5 ${i === active ? 'text-[#FFD108]' : 'text-muted-foreground'}`}
                      strokeWidth={2}
                    />
                    <span
                      className={`text-[10px] uppercase tracking-widest leading-tight ${
                        i === active ? 'text-[#FFD108]/90' : 'text-muted-foreground'
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Local keyframe for the progress bar */}
      <style>{`
        @keyframes tour-progress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes tour-callout-in {
          from { opacity: 0; transform: translateY(4px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 209, 8, 0.6); }
          50%      { box-shadow: 0 0 0 10px rgba(255, 209, 8, 0); }
        }
      `}</style>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Callout — pulsing yellow anchor dot + leader line + label card.
 * Positions in % of the parent's bounding box. Hidden on small screens.
 * ────────────────────────────────────────────────────────────────── */
function Callout({ x, y, anchor = 'right', overline, label, slideSlug, index }) {
  // Where the label sits relative to the dot
  const LABEL_OFFSET = 4; // % from the dot to where the label starts
  const labelStyle = { animationDelay: `${200 + index * 150}ms` };

  let labelPos = {};
  let leader = {};
  if (anchor === 'right') {
    labelPos = { left: `calc(${x}% + ${LABEL_OFFSET}%)`, top: `${y}%`, transform: 'translateY(-50%)' };
    leader = { left: `${x}%`, top: `${y}%`, width: `${LABEL_OFFSET}%`, height: '1px', transform: 'translateY(-50%)' };
  } else if (anchor === 'left') {
    labelPos = { right: `calc(${100 - x}% + ${LABEL_OFFSET}%)`, top: `${y}%`, transform: 'translateY(-50%)' };
    leader = { right: `${100 - x}%`, top: `${y}%`, width: `${LABEL_OFFSET}%`, height: '1px', transform: 'translateY(-50%)' };
  } else if (anchor === 'top') {
    labelPos = { left: `${x}%`, bottom: `calc(${100 - y}% + ${LABEL_OFFSET}%)`, transform: 'translateX(-50%)' };
    leader = { left: `${x}%`, bottom: `${100 - y}%`, height: `${LABEL_OFFSET}%`, width: '1px', transform: 'translateX(-50%)' };
  } else {
    // bottom
    labelPos = { left: `${x}%`, top: `calc(${y}% + ${LABEL_OFFSET}%)`, transform: 'translateX(-50%)' };
    leader = { left: `${x}%`, top: `${y}%`, height: `${LABEL_OFFSET}%`, width: '1px', transform: 'translateX(-50%)' };
  }

  return (
    <div
      className="absolute inset-0 hidden lg:block pointer-events-none"
      data-testid={`tour-callout-${slideSlug}-${index}`}
    >
      {/* Leader line */}
      <div
        className="absolute bg-gradient-to-r from-[#FFD108]/80 to-[#FFD108]/0"
        style={{ ...leader }}
      />

      {/* Anchor dot */}
      <span
        className="absolute w-2.5 h-2.5 rounded-full bg-[#FFD108] -translate-x-1/2 -translate-y-1/2"
        style={{
          left: `${x}%`,
          top: `${y}%`,
          animation: 'tour-pulse 2s ease-in-out infinite',
        }}
      />

      {/* Label card */}
      <div
        className="absolute px-3 py-2 rounded-sm bg-black/85 border border-[#FFD108]/30 backdrop-blur-sm shadow-[0_8px_24px_rgba(0,0,0,0.6)] min-w-[170px] max-w-[230px]"
        style={{
          ...labelPos,
          animation: 'tour-callout-in 500ms ease-out both',
          animationDelay: labelStyle.animationDelay,
        }}
      >
        <div className="text-[9px] uppercase tracking-widest text-[#FFD108]/90 font-mono mb-0.5">
          {overline}
        </div>
        <div className="text-[11px] leading-snug text-white/90">{label}</div>
      </div>
    </div>
  );
}
